/**
 * Offline sync service - handles queuing and syncing timesheet requests
 */

import { offlineStorage } from "./offlineStorage";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export interface SyncStatus {
  isOnline: boolean;
  pendingCount: number;
  lastSyncTime: number | null;
}

class OfflineSyncService {
  private syncStatus: SyncStatus = {
    isOnline: navigator.onLine,
    pendingCount: 0,
    lastSyncTime: null,
  };
  private listeners: Set<(status: SyncStatus) => void> = new Set();
  private syncInProgress = false;

  constructor() {
    // Initialize storage
    offlineStorage.init().catch(console.error);

    // Listen to online/offline events
    window.addEventListener("online", () => {
      // Verify online status with actual network check
      this.verifyOnlineStatus().then((isOnline) => {
        if (isOnline && !this.syncStatus.isOnline) {
          this.syncStatus.isOnline = true;
          this.notifyListeners();
          // Only sync if we have a token
          if (localStorage.getItem("token")) {
            this.syncAll();
          }
        }
      });
    });

    window.addEventListener("offline", () => {
      this.syncStatus.isOnline = false;
      this.notifyListeners();
    });

    // Verify online status on initialization (navigator.onLine can be unreliable)
    this.verifyOnlineStatus().then((isOnline) => {
      if (isOnline !== this.syncStatus.isOnline) {
        this.syncStatus.isOnline = isOnline;
        this.notifyListeners();
      }

      // Initial sync check - only sync if we have a token and are actually online
      this.updatePendingCount();
      if (this.syncStatus.isOnline && localStorage.getItem("token")) {
        this.syncAll();
      }
    });

    // Periodic sync check when online (every 30 seconds)
    setInterval(() => {
      if (this.syncStatus.isOnline && !this.syncInProgress) {
        this.updatePendingCount().then(() => {
          if (this.syncStatus.pendingCount > 0) {
            console.log(
              "[OfflineSync] Periodic sync check: pending items found, starting sync...",
            );
            this.syncAll();
          }
        });
      }
    }, 30000);
  }

  /**
   * Verify online status by attempting a network request
   * navigator.onLine can be unreliable, especially on initial page load
   */
  private async verifyOnlineStatus(): Promise<boolean> {
    // If navigator.onLine says offline, trust it
    if (!navigator.onLine) {
      return false;
    }

    // Try to verify with a lightweight network request
    try {
      // Use a HEAD request to minimize data transfer
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout

      const response = await fetch(`${API_URL}/api/health`, {
        method: "HEAD",
        signal: controller.signal,
        cache: "no-cache",
        headers: {
          "Cache-Control": "no-cache",
        },
      }).catch(() => null);

      clearTimeout(timeoutId);

      // If we get any response (even 404/500), we're online
      // Network errors (null response) mean we're offline
      return response !== null;
    } catch (error) {
      // Network error means we're offline
      return false;
    }
  }

  /**
   * Save timesheet - tries online first, falls back to offline storage
   */
  async saveTimesheet(
    timesheetData: any,
    status: "draft" | "submitted",
    token: string,
  ): Promise<{
    success: boolean;
    offlineId?: string;
    error?: string;
    data?: any;
  }> {
    if (this.syncStatus.isOnline) {
      try {
        // Try online save
        const response = await fetch(`${API_URL}/api/timesheets`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(timesheetData),
        });

        if (response.ok) {
          const responseData = await response.json();
          // Return the timesheet data if available
          return {
            success: true,
            data: responseData.data || responseData.timesheet || null,
          };
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to save timesheet");
        }
      } catch (error) {
        // Network error - save offline
        console.warn("[OfflineSync] Network error, saving offline:", error);
        return this.saveOffline(timesheetData, status);
      }
    } else {
      // Already offline - save directly
      return this.saveOffline(timesheetData, status);
    }
  }

  /**
   * Save any API request - tries online first, falls back to offline queue
   */
  async saveRequest(
    url: string,
    method: string,
    data: any,
    token: string,
  ): Promise<{ success: boolean; error?: string }> {
    if (this.syncStatus.isOnline) {
      try {
        // Try online save
        const response = await fetch(url, {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });

        if (response.ok) {
          // Verify response body indicates success
          try {
            const responseData = await response.json();
            console.log("[OfflineSync] Response data:", responseData);
            if (responseData.success === false) {
              // Backend returned success: false in body
              const errorMsg = responseData.message || "Request failed";
              console.error("[OfflineSync] Backend returned error:", errorMsg);
              return { success: false, error: errorMsg };
            }
            console.log(
              "[OfflineSync] Request succeeded, response:",
              responseData,
            );
            return { success: true };
          } catch (parseError) {
            // Response is ok but not JSON - assume success
            console.warn("[OfflineSync] Response ok but not JSON:", parseError);
            return { success: true };
          }
        } else {
          // HTTP error status
          let errorData;
          try {
            errorData = await response.json();
          } catch {
            errorData = {
              message: `HTTP ${response.status}: ${response.statusText}`,
            };
          }
          const errorMsg =
            errorData.message || `Failed to save request (${response.status})`;
          console.error("[OfflineSync] HTTP error:", response.status, errorMsg);

          // Don't queue 4xx errors (client errors) - they won't succeed on retry
          if (response.status >= 400 && response.status < 500) {
            return { success: false, error: errorMsg };
          }

          // Queue 5xx errors (server errors) for retry
          throw new Error(errorMsg);
        }
      } catch (error) {
        // Check if it's a CORS error (TypeError with specific message)
        const isCorsError =
          error instanceof TypeError &&
          (error.message.includes("Failed to fetch") ||
            error.message.includes("network") ||
            error.message.includes("CORS"));

        if (isCorsError) {
          console.error("[OfflineSync] CORS error detected:", error);
          return {
            success: false,
            error:
              "CORS error: Request blocked by browser. Please check server configuration.",
          };
        }

        // Network error - queue offline
        console.warn("[OfflineSync] Network error, queuing offline:", error);
        return this.queueOffline(url, method, data, token);
      }
    } else {
      // Already offline - queue directly
      return this.queueOffline(url, method, data, token);
    }
  }

  /**
   * Queue request for offline sync
   */
  private async queueOffline(
    url: string,
    method: string,
    data: any,
    token: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      await offlineStorage.queueRequest({
        url,
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      // Register background sync if available
      if ("serviceWorker" in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          if ("sync" in registration) {
            await (registration as any).sync.register("sync-timesheets");
          }
        } catch (err) {
          console.warn("[OfflineSync] Background sync not available:", err);
        }
      }

      await this.updatePendingCount();
      return { success: true };
    } catch (error) {
      console.error("[OfflineSync] Error queuing offline:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to queue offline",
      };
    }
  }

  /**
   * Save timesheet to offline storage and queue for sync
   */
  private async saveOffline(
    timesheetData: any,
    status: "draft" | "submitted",
  ): Promise<{ success: boolean; offlineId?: string; error?: string }> {
    try {
      // Save to IndexedDB
      const offlineId = await offlineStorage.saveTimesheet(
        timesheetData,
        status,
      );

      // Queue the request for background sync
      const token = localStorage.getItem("token");
      if (token) {
        await offlineStorage.queueRequest({
          url: `${API_URL}/api/timesheets`,
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(timesheetData),
        });

        // Register background sync if available
        if ("serviceWorker" in navigator) {
          try {
            const registration = await navigator.serviceWorker.ready;
            if ("sync" in registration) {
              await (registration as any).sync.register("sync-timesheets");
            }
          } catch (err) {
            console.warn("[OfflineSync] Background sync not available:", err);
          }
        }
      }

      await this.updatePendingCount();
      return { success: true, offlineId };
    } catch (error) {
      console.error("[OfflineSync] Error saving offline:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to save offline",
      };
    }
  }

  /**
   * Sync all pending timesheets
   */
  async syncAll(): Promise<void> {
    if (this.syncInProgress || !this.syncStatus.isOnline) {
      console.log("[OfflineSync] Sync skipped:", {
        syncInProgress: this.syncInProgress,
        isOnline: this.syncStatus.isOnline,
      });
      return;
    }

    this.syncInProgress = true;
    console.log("[OfflineSync] Starting sync...");

    try {
      const unsyncedTimesheets =
        await offlineStorage.getAllUnsyncedTimesheets();
      const queuedRequests = await offlineStorage.getQueuedRequests();

      console.log(
        `[OfflineSync] Syncing ${unsyncedTimesheets.length} timesheets and ${queuedRequests.length} requests`,
      );

      const token = localStorage.getItem("token");
      if (!token) {
        // No token is expected during login/signup - silently skip sync
        this.syncInProgress = false;
        await this.updatePendingCount();
        return;
      }

      // Sync timesheets
      for (const timesheet of unsyncedTimesheets) {
        try {
          const response = await fetch(`${API_URL}/api/timesheets`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(timesheet.data),
          });

          if (response.ok) {
            await offlineStorage.markTimesheetSynced(timesheet.id);
            console.log(`[OfflineSync] ✅ Synced timesheet ${timesheet.id}`);
          } else {
            const errorText = await response
              .text()
              .catch(() => "Unknown error");
            console.error(
              `[OfflineSync] ❌ Failed to sync timesheet ${timesheet.id}:`,
              response.status,
              errorText,
            );

            // If auth error, don't retry - user needs to log in again
            if (response.status === 401 || response.status === 403) {
              console.warn(
                `[OfflineSync] Auth error for timesheet ${timesheet.id}, marking as synced to prevent retry loop`,
              );
              await offlineStorage.markTimesheetSynced(timesheet.id);
            }
          }
        } catch (error) {
          console.error(
            `[OfflineSync] ❌ Error syncing timesheet ${timesheet.id}:`,
            error,
          );
          // Network errors will be retried on next sync
        }
      }

      // Sync queued requests
      for (const queuedRequest of queuedRequests) {
        try {
          const response = await fetch(queuedRequest.url, {
            method: queuedRequest.method,
            headers: queuedRequest.headers,
            body: queuedRequest.body,
          });

          if (response.ok && queuedRequest.id) {
            await offlineStorage.removeQueuedRequest(queuedRequest.id);
            console.log(
              `[OfflineSync] ✅ Synced queued request ${queuedRequest.id}`,
            );
          } else {
            const errorText = await response
              .text()
              .catch(() => "Unknown error");
            let errorData;
            try {
              errorData = JSON.parse(errorText);
            } catch {
              errorData = { message: errorText };
            }
            console.error(
              `[OfflineSync] ❌ Failed to sync queued request ${queuedRequest.id}:`,
              response.status,
              errorData,
            );

            // Remove from queue if it's a client error (4xx) that won't succeed on retry
            // 400 = Bad Request (validation errors, insufficient balance, etc.)
            // 401 = Unauthorized (auth expired)
            // 403 = Forbidden (permission denied)
            // 404 = Not Found (resource doesn't exist)
            if (
              response.status >= 400 &&
              response.status < 500 &&
              queuedRequest.id
            ) {
              console.warn(
                `[OfflineSync] Client error (${response.status}) for queued request ${queuedRequest.id}, removing from queue to prevent retry loop`,
              );
              await offlineStorage.removeQueuedRequest(queuedRequest.id);
            }
            // 5xx errors (server errors) will be retried on next sync
          }
        } catch (error) {
          console.error(
            `[OfflineSync] ❌ Error syncing queued request:`,
            error,
          );
          // Network errors will be retried on next sync
        }
      }

      this.syncStatus.lastSyncTime = Date.now();
      await this.updatePendingCount();
      console.log(
        "[OfflineSync] Sync completed. Pending count:",
        this.syncStatus.pendingCount,
      );
    } catch (error) {
      console.error("[OfflineSync] Error in syncAll:", error);
    } finally {
      this.syncInProgress = false;
      // Ensure pending count is updated even if sync failed
      await this.updatePendingCount();
    }
  }

  /**
   * Update pending count
   */
  private async updatePendingCount(): Promise<void> {
    try {
      const unsynced = await offlineStorage.getAllUnsyncedTimesheets();
      const queued = await offlineStorage.getQueuedRequests();
      this.syncStatus.pendingCount = unsynced.length + queued.length;
      this.notifyListeners();
    } catch (error) {
      console.error("[OfflineSync] Error updating pending count:", error);
    }
  }

  /**
   * Get current sync status
   */
  getStatus(): SyncStatus {
    return { ...this.syncStatus };
  }

  /**
   * Subscribe to status changes
   */
  subscribe(listener: (status: SyncStatus) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.getStatus());
      } catch (error) {
        console.error("[OfflineSync] Error in listener:", error);
      }
    });
  }
}

export const offlineSync = new OfflineSyncService();
